// SPDX-License-Identifier: MIT
/** GLSL ES 3.00 sources for the post chain (§1.2: motion blur, two-level bloom, aberration, ...). */
export const FULLSCREEN_VS = `#version 300 es
out vec2 vUv;
void main() {
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  vUv = p;
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;
const HEADER = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 outColor;
`;
/**
 * One motion-blur subframe: sRGB scene → linear-light (gamma 2.0 approximation, which SwiftShader
 * evaluates far cheaper than pow 2.2), weighted, added into the float accumulator.
 */
export const ACCUMULATE_FS = `${HEADER}
uniform sampler2D uScene;
uniform float uWeight;
void main() {
  vec3 c = texture(uScene, vUv).rgb;
  outColor = vec4(c * c * uWeight, uWeight);
}`;
/**
 * One subframe of the crisp layer (text that skips bloom, aberration and distortion). The layer is
 * transparent; it accumulates premultiplied linear colour and coverage.
 */
export const ACCUMULATE_OVERLAY_FS = `${HEADER}
uniform sampler2D uScene;
uniform float uWeight;
void main() {
  vec4 s = texture(uScene, vUv);
  outColor = vec4(s.rgb * s.rgb * s.a, s.a) * uWeight;
}`;
/** Soft-knee bright pass into the half-resolution bloom chain. */
export const BRIGHT_FS = `${HEADER}
uniform sampler2D uSrc;
uniform float uThreshold;
void main() {
  vec3 c = texture(uSrc, vUv).rgb;
  float l = max(c.r, max(c.g, c.b));
  float knee = uThreshold * 0.5;
  float soft = clamp(l - uThreshold + knee, 0.0, 2.0 * knee);
  soft = soft * soft / (4.0 * knee + 1e-4);
  float contrib = max(soft, l - uThreshold) / max(l, 1e-4);
  outColor = vec4(c * contrib, 1.0);
}`;
/** 9-tap separable Gaussian using linear filtering (5 fetches). */
export const BLUR_FS = `${HEADER}
uniform sampler2D uSrc;
uniform vec2 uDir;
void main() {
  vec3 c = texture(uSrc, vUv).rgb * 0.2270270270;
  c += texture(uSrc, vUv + uDir * 1.3846153846).rgb * 0.3162162162;
  c += texture(uSrc, vUv - uDir * 1.3846153846).rgb * 0.3162162162;
  c += texture(uSrc, vUv + uDir * 3.2307692308).rgb * 0.0702702703;
  c += texture(uSrc, vUv - uDir * 3.2307692308).rgb * 0.0702702703;
  outColor = vec4(c, 1.0);
}`;
export const COPY_FS = `${HEADER}
uniform sampler2D uSrc;
void main() { outColor = vec4(texture(uSrc, vUv).rgb, 1.0); }`;
/**
 * Final composite: lens distortion, chromatic aberration, bloom, crisp layer, flash, vignette,
 * highlight shoulder, gamma encode, grain. Written for CPU rasterisation (SwiftShader): no pow/exp, a
 * polynomial vignette and a noise texture instead of a per-pixel hash.
 */
export const COMPOSITE_FS = `${HEADER}
uniform sampler2D uAccum;
uniform sampler2D uBloomHalf;
uniform sampler2D uBloomQuarter;
uniform sampler2D uNoise;
uniform sampler2D uOverlay;
uniform float uOverlayOn;
uniform vec2 uBloom;
uniform float uAberration;
uniform float uDistortion;
uniform float uVignette;
uniform float uGrain;
uniform float uFlash;
uniform vec2 uNoiseOffset;
uniform float uAspect;
uniform float uExposure;
uniform float uContrast;
uniform float uSaturation;
uniform vec3 uTint;

void main() {
  vec2 c = vUv - 0.5;
  float r2 = dot(c, c);
  vec2 uv = 0.5 + c * (1.0 - uDistortion * r2);
  vec3 col;
  if (uAberration > 0.0) {
    vec2 off = c * uAberration * (1.0 + 3.0 * r2);
    col = vec3(texture(uAccum, uv + off).r, texture(uAccum, uv).g, texture(uAccum, uv - off).b);
  } else {
    col = texture(uAccum, uv).rgb;
  }
  col += texture(uBloomHalf, uv).rgb * uBloom.x + texture(uBloomQuarter, uv).rgb * uBloom.y;
  // Crisp layer: over the lens effects, under the grade, so text stays sharp in the same colour world.
  if (uOverlayOn > 0.0) {
    vec4 ov = texture(uOverlay, vUv);
    col = col * (1.0 - ov.a) + ov.rgb;
  }
  col *= uExposure * uTint;
  vec2 cv = c * vec2(1.0, uAspect);
  float v = clamp(dot(cv, cv) * 2.4 - 0.12, 0.0, 1.0);
  col *= 1.0 - uVignette * v * v * (3.0 - 2.0 * v);
  vec3 over = max(col - 0.8, 0.0);
  col = sqrt(min(col, 0.8) + over / (1.0 + 5.0 * over));
  // Grade in display space: saturation around luma, contrast around mid-grey.
  float luma = dot(col, vec3(0.2126, 0.7152, 0.0722));
  col = mix(vec3(luma), col, uSaturation);
  col = (col - 0.5) * uContrast + 0.5;
  // Flash is mixed in display space so its strength reads linearly (0.1 = 10% towards white);
  // mixed in linear light, gamma encoding would turn a 7% flash into a ~28% grey wash.
  col = mix(col, vec3(1.0), uFlash);
  col += (texture(uNoise, gl_FragCoord.xy * (1.0 / 256.0) + uNoiseOffset).r - 0.5) * uGrain;
  outColor = vec4(col, 1.0);
}`;
/**
 * Packs the composited RGBA8 frame into planar yuv420p bytes (BT.709, limited range), four bytes per
 * output texel, so the host can read a ready-to-encode frame with one readPixels (2.7× less data than
 * RGBA). Output row r (from the bottom of the target) is byte row r of the yuv420p buffer.
 */
export const YUV420_FS = `#version 300 es
precision highp float;
precision highp int;
uniform sampler2D uSrc;
uniform ivec2 uSize;
out vec4 outColor;

vec3 px(int x, int y) { return texelFetch(uSrc, ivec2(x, uSize.y - 1 - y), 0).rgb; }
float luma(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }

float byteAt(int idx) {
  int w = uSize.x;
  int h = uSize.y;
  int ySize = w * h;
  if (idx < ySize) return (16.0 + 219.0 * luma(px(idx % w, idx / w))) / 255.0;
  int cw = w / 2;
  int j = idx - ySize;
  bool isV = j >= ySize / 4;
  if (isV) j -= ySize / 4;
  int cx = (j % cw) * 2;
  int cy = (j / cw) * 2;
  vec3 c = (px(cx, cy) + px(cx + 1, cy) + px(cx, cy + 1) + px(cx + 1, cy + 1)) * 0.25;
  float y = luma(c);
  float chroma = isV ? (c.r - y) / 1.5748 : (c.b - y) / 1.8556;
  return (128.0 + 224.0 * chroma) / 255.0;
}

void main() {
  int base = int(gl_FragCoord.y) * uSize.x + int(gl_FragCoord.x) * 4;
  outColor = vec4(byteAt(base), byteAt(base + 1), byteAt(base + 2), byteAt(base + 3));
}`;
